<template>
  <div class="card mb-4 radius-7">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 radius-7">
      <h5 class="card-title mb-0">
        <i class="ri-time-line me-2"></i>Horário de Funcionamento
      </h5>
    </div>
    <div class="card-body">
      <!-- Estado de carregamento -->
      <div v-if="loadingOperatingHours" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>

      <!-- Erro ao carregar -->
      <div v-else-if="operatingHoursError" class="alert alert-danger" role="alert">
        <i class="ri-alert-line me-2"></i>{{ operatingHoursError }}
      </div>

      <!-- Visualização dos horários cadastrados -->
      <div v-else>
        <!-- Modo de visualização -->
        <div v-if="!isEditing" class="mb-4">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted">Horário de Abertura</label>
                <div class="bg-white">
                  <h6 class="mb-0">
                    <i class="ri-login-box-line me-2 text-success"></i>
                    <strong>{{ operatingHours.opening_time || '--:--' }}</strong>
                  </h6>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted">Horário de Fechamento</label>
                <div class="bg-white">
                  <h6 class="mb-0">
                    <i class="ri-logout-box-line me-2 text-danger"></i>
                    <strong>{{ operatingHours.closing_time || '--:--' }}</strong>
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modo de edição -->
        <div v-if="isEditing" class="mb-4">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label for="opening_time" class="form-label">
                  <i class="ri-login-box-line me-2 text-success"></i>Horário de Abertura
                </label>
                <input
                  id="opening_time"
                  v-model="editingHours.opening_time"
                  type="time"
                  class="form-control"
                  :disabled="savingOperatingHours"
                />
                <small class="text-muted d-block mt-1">Ex: 08:00</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label for="closing_time" class="form-label">
                  <i class="ri-logout-box-line me-2 text-danger"></i>Horário de Fechamento
                </label>
                <input
                  id="closing_time"
                  v-model="editingHours.closing_time"
                  type="time"
                  class="form-control"
                  :disabled="savingOperatingHours"
                />
                <small class="text-muted d-block mt-1">Ex: 21:00</small>
              </div>
            </div>
          </div>

          <!-- Validação -->
          <div v-if="validationError" class="alert alert-warning" role="alert">
            <i class="ri-alert-line me-2"></i>{{ validationError }}
          </div>
        </div>

        <!-- Botões de ação -->
        <div class="d-flex gap-2">
          <button
            v-if="!isEditing"
            type="button"
            class="btn btn-primary"
            @click="startEditing"
            :disabled="loadingOperatingHours"
          >
            <i class="ri-edit-line me-2"></i>Editar Horário
          </button>

          <template v-else>
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelEditing"
              :disabled="savingOperatingHours"
            >
              <i class="ri-close-line me-2"></i>Cancelar
            </button>
            
            <button
              type="button"
              class="btn btn-success"
              @click="handleSaveOperatingHours"
              :disabled="savingOperatingHours || validationError"
            >
              <span
                v-if="savingOperatingHours"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="ri-save-line me-2"></i>
              {{ savingOperatingHours ? 'Salvando...' : 'Salvar' }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useStore } from "vuex";
import { computed, ref, onMounted, watch } from "vue";

interface OperatingHours {
  opening_time: string;
  closing_time: string;
}

export default defineComponent({
  name: "OperatingHoursConfig",
  setup() {
    const store = useStore();
    const isEditing = ref(false);
    const editingHours = ref<OperatingHours>({
      opening_time: "",
      closing_time: "",
    });
    const validationError = ref<string | null>(null);

    const operatingHours = computed(() => {
      return store.getters.getOperatingHours || {
        opening_time: "",
        closing_time: "",
      };
    });

    const loadingOperatingHours = computed(() => {
      return store.getters.getLoadingOperatingHours || false;
    });

    const savingOperatingHours = computed(() => {
      return store.getters.getSavingOperatingHours || false;
    });

    const operatingHoursError = computed(() => {
      return store.getters.getOperatingHoursError || null;
    });

    const validateTimes = () => {
      validationError.value = null;

      if (!editingHours.value.opening_time || !editingHours.value.closing_time) {
        validationError.value = "Preencha ambos os horários";
        return false;
      }

      if (editingHours.value.opening_time >= editingHours.value.closing_time) {
        validationError.value =
          "O horário de abertura deve ser anterior ao de fechamento";
        return false;
      }

      return true;
    };

    const startEditing = () => {
      editingHours.value = JSON.parse(JSON.stringify(operatingHours.value));
      isEditing.value = true;
    };

    const cancelEditing = () => {
      isEditing.value = false;
      editingHours.value = {
        opening_time: "",
        closing_time: "",
      };
      validationError.value = null;
    };

    const handleSaveOperatingHours = async () => {
      if (!validateTimes()) {
        return;
      }

      try {
        await store.dispatch("saveOperatingHours", editingHours.value);
        isEditing.value = false;
        editingHours.value = {
          opening_time: "",
          closing_time: "",
        };
        validationError.value = null;
      } catch (error) {
        console.error("Erro ao salvar horários:", error);
      }
    };

    // Validar ao digitar
    watch(
      () => editingHours.value,
      () => {
        if (isEditing.value && editingHours.value.opening_time && editingHours.value.closing_time) {
          validateTimes();
        }
      },
      { deep: true }
    );

    onMounted(() => {
      store.dispatch("fetchOperatingHours");
    });

    return {
      isEditing,
      editingHours,
      savingOperatingHours,
      validationError,
      operatingHours,
      loadingOperatingHours,
      operatingHoursError,
      startEditing,
      cancelEditing,
      handleSaveOperatingHours,
    };
  },
});
</script>

<style scoped>
.card {
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.btn-primary:hover {
  background-color: #0c63e4;
}

.btn-success:hover {
  background-color: #0d6e3a;
}

input[type="time"] {
  font-size: 1rem;
}

input[type="time"]:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}
</style>
